import { Suspense, lazy, useState } from "react";
import { TargetedAtType } from "../../config/enums";
import { Button } from "../../elements/Button";
import { CancelButton } from "../../elements/CancelButton";
import { ChoiceList } from "../../elements/ChoiceList";
import { Modal } from "../../elements/Modal";
import { FilterIcon, MagnifyIcon, MinusIcon, PlusIcon } from "../../icons";
import { useEntityPage } from "../useEntityPage";
import { notifyMarketingListMembersChanged } from "./marketingListMembersChanged";

// Lazy: these reach the router, which the registry importing this must not.
const AddMarketingListMembersModal = lazy(() =>
  import("./AddMarketingListMembersModal").then((module) => ({
    default: module.AddMarketingListMembersModal,
  }))
);
const MarketingListSearchPanel = lazy(() =>
  import("./MarketingListSearchPanel").then((module) => ({
    default: module.MarketingListSearchPanel,
  }))
);

type View = "choices" | "lookup" | "add" | "remove" | null;

/** The Members section's button, top right of its card, for a list that has been saved. */
export function ManageMarketingListMembers() {
  const { record, isNew, canEdit } = useEntityPage();
  const [view, setView] = useState<View>(null);
  const listId = record?.id ? String(record.id) : undefined;

  if (isNew || !listId || !canEdit) {
    return null;
  }

  // The advanced search is for contacts, so only a list of contacts is offered it.
  const offersSearch = String(record?.createdfromcode) === String(TargetedAtType.Contact);
  const close = () => setView(null);
  const done = () => {
    close();
    notifyMarketingListMembersChanged();
  };

  return (
    <>
      <Button
        variant="none"
        className="border rounded-2 bg-white text-dark fw-medium hover-bg-light d-flex align-items-center"
        icon={PlusIcon}
        text="Manage members"
        onClick={() => setView(offersSearch ? "choices" : "lookup")}
      />
      {view === "choices" && (
        <Modal title="Manage Members" onClose={close}>
          <div className="modal-body">
            <p>How would you like to manage the members of this list?</p>
            <ChoiceList
              choices={[
                {
                  title: "Add using lookup",
                  description: "Find contacts by name or email address and add them",
                  icon: MagnifyIcon,
                  iconClassName: "text-primary",
                  onClick: () => setView("lookup"),
                },
                {
                  title: "Add using advanced search",
                  description: "Build a search and add the matching contacts",
                  icon: FilterIcon,
                  iconClassName: "text-primary",
                  onClick: () => setView("add"),
                },
                {
                  title: "Remove using advanced search",
                  description: "Build a search and remove the matching members",
                  icon: MinusIcon,
                  iconClassName: "text-danger",
                  onClick: () => setView("remove"),
                },
              ]}
            />
          </div>
          <div className="modal-footer d-block">
            <CancelButton onClick={close} />
          </div>
        </Modal>
      )}
      <Suspense fallback={null}>
        {view === "lookup" && (
          <AddMarketingListMembersModal listId={listId} onClose={close} onAdded={done} />
        )}
        {(view === "add" || view === "remove") && (
          <MarketingListSearchPanel
            listId={listId}
            mode={view}
            onClose={close}
            onComplete={done}
          />
        )}
      </Suspense>
    </>
  );
}
